// prettier-ignore
import MapGL, { GeolocateControl, Marker, NavigationControl } from '@urbica/react-map-gl';
// prettier-ignore
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';


import PoiMarker from './components/Map/PoiMarker';
import { Layers, Modals } from './stores/layers';
import { MapOverlay } from './components/mapOverlay';
import { higestRankedCategory } from './helpers/poiHelper';
import { useStore } from './stores';
import { useTranslateFunction } from './helpers/useTranslationHook';
import { PageLoader } from './components/Loader';

const MapContext = createContext();

const ZOOMOUT_REFRESH_THRESHOLD = 1.5;
const DEFAULT_ZOOM = 14;

const defaultClassNames =
  'fixed top-[64px] left-0 bottom-[90px] right-0 lg:top-0 lg:bottom-0 lg:pl-[300px]';

export function useMap() {
  return useContext(MapContext)?.current?.getMap() || null;
}

export const Map = observer(({ children, ...props }) => {
  const {
    config,
    poi,
    categories,
    favourites,
    layers,
    user,
    parking,
    myLocations,
  } = useStore();

  const mapReference = useRef();
  const [zoomBaseline, setZoomBaseline] = useState(DEFAULT_ZOOM);
  const { t } = useTranslateFunction('parkinglayer');

  useEffect(() => {
    async function initalLoad() {
      try {
        await categories.fetch();
        favourites.fetch();
        myLocations.fetch();
        mapReference.current?.getMap()?.on('moveend', (event) => {
          if (
            mapReference?.current?.getMap()?.getZoom() >
            config.mapBox?.maxZoomLevel
          ) {
            poi.fetch(mapReference.current?.getMap()?.getBounds());
          } else {
            poi.clear();
          }
        });
      } catch (error) {
        console.log(error);
      }
    }
    mapReference.current?.getMap()?.once('load', async () => {
      mapReference.current?.getMap()?.resize();
    });
    mapReference.current?.getMap()?.once('idle', async () => {
      mapReference.current?.getMap()?.resize();
    });
    initalLoad();
  }, [poi, categories, favourites]);

  const mapboxProps = {
    mapStyle: config.mapBox.styleUrl,
    accessToken: config.mapBox.accessToken,
    latitude: config.initialPosition.latitude,
    longitude: config.initialPosition.longitude,
    zoom: DEFAULT_ZOOM,
  };

  const onClick = (event) => {
    const { lngLat } = event;
    if (myLocations.addingLocationFlow) {
      layers.openModal(Modals.addMyLocations);
      myLocations.selectNewPoi([
        parseFloat(lngLat.lat),
        parseFloat(lngLat.lng),
      ]);
    }
  };

  const firstTwentyParkingSpots =
    parking?.parkopedia?.parkingSpots?.data?.slice(0, 20);

  const classNames = props?.className ?? defaultClassNames;

  return (
    <div className={classNames}>
      <MapOverlay mapReference={mapReference} />
      <MapGL
        className="relative"
        onClick={onClick}
        {...mapboxProps}
        onViewportChange={() => {
          const currentZoom = mapReference?.current?.getMap()?.getZoom();
          // zoom out
          if (zoomBaseline - ZOOMOUT_REFRESH_THRESHOLD > currentZoom) {
            setZoomBaseline(currentZoom);
            poi.setClearFlag(true);
          }

          // zoom in
          if (zoomBaseline + 0.5 < currentZoom) {
            setZoomBaseline(currentZoom);
          }
        }}
        ref={mapReference}
        // TODD make configurable
        attributionControl={false}
      >
        {/* <UserMarker /> */}
        {poi.list.map((item) => (
          <Marker
            latitude={item.location.coordinates[1]}
            longitude={item.location.coordinates[0]}
            onClick={() => {
              poi.setActive(JSON.parse(JSON.stringify(item)));
              layers.openLayer(Layers.info);
            }}
            key={item.id}
          >
            <PoiMarker
              category={higestRankedCategory(item)}
              active={item.id === poi?.active?.id}
            ></PoiMarker>
          </Marker>
        ))}

        {myLocations?.list?.map((item) => (
          <Marker
            latitude={item.location.coordinates[1]}
            longitude={item.location.coordinates[0]}
            onClick={() => {
              poi.setActive(JSON.parse(JSON.stringify(item)));
              layers.openLayer(Layers.info);
            }}
            key={item.id}
          >
            <PoiMarker
              category={higestRankedCategory(item)}
              active={item.id === poi?.active?.id}
            ></PoiMarker>
          </Marker>
        ))}

        {parking?.parkopedia?.parkingSpots?.status === 'loading' ? (
          <PageLoader variant="absolute" info={t('parkopedia-fetch-loading')} />
        ) : null}

        {firstTwentyParkingSpots?.map((item) => {
          const [longitude, latitude] = item.geometry.geometries.coordinates;
          return (
            <Marker
              latitude={latitude}
              longitude={longitude}
              onClick={() => {
                poi.setInactive();
                parking.setPosition({ coordinates: [longitude, latitude] });
                parking.setInfo(item?.properties?.static);
                layers.openLayer(Layers.parking);
              }}
              key={item?.properties?.static?.url}
            >
              <PoiMarker
                category={{ name: 'parking', color: '#175ECB', id: -1 }}
                active={false}
              ></PoiMarker>
            </Marker>
          );
        })}
        <NavigationControl
          showCompass={user.config.showCompassControl}
          showZoom={user.config.showZoomControl}
          position="bottom-right"
        />
        {user.config.showPositionControl ? (
          <GeolocateControl
            position="bottom-left"
            positionOptions={{ enableHighAccuracy: true, timeout: 6000 }}
            onGeolocate={(event) => {
              const { longitude, latitude } = event.coords;
              user.setPosition({ longitude, latitude });
            }}
          />
        ) : null}
      </MapGL>
      <MapContext.Provider value={mapReference}>{children}</MapContext.Provider>
    </div>
  );
});
